
import React from 'react';
import { Card, CardContent, CardHeader, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { format } from "date-fns";
import { Tables } from "@/integrations/supabase/types";
import QuoteStatusIndicator from "./QuoteStatusIndicator";
import QuoteActionsDropdown from "./QuoteActionsDropdown";
import QuoteActionButtons from "./QuoteActionButtons";
import QuotePaymentButtons from "./QuotePaymentButtons";
import { FileText, Calendar, ArrowRight, Eye, ExternalLink, Hash } from "lucide-react";
import { supabase } from '@/integrations/supabase/client';
import { useUserRoles } from '@/hooks/useUserRoles';

type Quote = Tables<'quotes'> & {
  external_identifier?: string | null;
};

interface QuoteCardProps {
  quote: Quote;
  isChild?: boolean;
  canInteractWithQuotes: boolean;
  isAdminViewingOthersBooking: boolean;
  loadingQuoteId: string | null;
  generatingPayment: string | null;
  onQuoteResponse: (quoteId: string, action: 'accepted' | 'rejected', response?: string) => void;
  onDeleteQuote: (quoteId: string) => void;
  onReviseQuote?: (quote: Quote) => void;
  onAddVariation?: (quote: Quote) => void;
  onPaymentUpdate?: () => void;
}

const QuoteCard: React.FC<QuoteCardProps> = ({
  quote,
  isChild = false,
  canInteractWithQuotes,
  isAdminViewingOthersBooking,
  loadingQuoteId,
  generatingPayment,
  onQuoteResponse,
  onDeleteQuote,
  onReviseQuote,
  onAddVariation,
  onPaymentUpdate
}) => {
  const { roles } = useUserRoles();

  const formatCurrency = (amount: number | null) => {
    if (!amount) return 'No amount specified';
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: quote.currency || 'ZAR'
    }).format(amount);
  };

  const getQuoteTypeDisplay = () => {
    switch (quote.quote_type) {
      case 'revised':
        return 'Revised Quote';
      case 'variation':
        return 'Variation Quote';
      default:
        return 'Initial Quote';
    }
  };

  const getQuoteTypeColor = () => {
    switch (quote.quote_type) {
      case 'revised':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'variation':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      default:
        return 'bg-blue-100 text-blue-800 border-blue-200';
    }
  };

  const handleViewFile = async () => {
    if (!quote.quote_file_url) return;
    try {
      const { data } = await supabase.storage.from('quote-documents').createSignedUrl(quote.quote_file_url, 3600);
      if (data?.signedUrl) {
        window.open(data.signedUrl, '_blank', 'noopener,noreferrer');
      }
    } catch (error) {
      console.error('Error viewing file:', error);
    }
  };

  const handleViewLink = () => {
    if (quote.quote_link) {
      window.open(quote.quote_link, '_blank', 'noopener,noreferrer');
    }
  };

  const isQuoteFinalized = quote.status === 'accepted' || quote.status === 'rejected';
  const isQuoteAccepted = quote.status === 'accepted';

  return (
    <Card className={`transition-all duration-200 hover:shadow-lg border-border/40 ${isChild ? 'ml-6 border-l-4 border-blue-200 bg-blue-50/30' : ''}`}>
      <CardHeader className="pb-3">
        {isChild && (
          <div className="flex items-center gap-2 text-xs text-blue-600 mb-2">
            <ArrowRight className="w-3 h-3" />
            <span className="font-medium">
              {quote.quote_type === 'revised' ? 'Revision' : 'Variation'}
            </span>
          </div>
        )}
        
        <div className="flex flex-col gap-3">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 space-y-2">
              <div className="flex items-center gap-2 flex-wrap">
                {quote.external_identifier ? (
                  <Badge variant="outline" className="font-medium text-xs md:text-sm border-primary bg-primary/5">
                    <Hash className="w-3 h-3 mr-1" />
                    {quote.external_identifier}
                  </Badge>
                ) : (
                  <Badge variant="secondary" className="font-medium text-xs md:text-sm">
                    Quote: #{quote.id.slice(-8)}
                  </Badge>
                )}
                <Badge className={`text-xs font-medium border ${getQuoteTypeColor()}`}>
                  {getQuoteTypeDisplay()}
                </Badge>
              </div>
              
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Calendar className="w-3 h-3" />
                <span>{format(new Date(quote.created_at), 'MMM dd, yyyy')}</span>
              </div>
            </div>
            
            <div className="text-right">
              <div className="text-xl md:text-2xl font-bold text-foreground">
                {formatCurrency(quote.quoted_amount)}
              </div>
            </div>
          </div>
          
          {quote.submission_notes && (
            <div className="bg-muted/30 rounded p-2 border-l-2 border-primary/20">
              <p className="text-xs leading-relaxed">{quote.submission_notes}</p>
            </div>
          )}

          {quote.quote_type === 'variation' && quote.variation_reason && (
            <div className="bg-purple-50 border border-purple-200 rounded p-2">
              <p className="text-xs font-medium text-purple-700 mb-1">Variation Reason</p>
              <p className="text-xs text-purple-900">{quote.variation_reason}</p>
            </div>
          )}
        </div>

        {/* Status badges and action buttons row */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mt-3">
          <div className="flex items-center gap-2">
            <QuoteStatusIndicator 
              viewed={!!quote.customer_viewed_at} 
              viewCount={quote.view_count || 0} 
              viewedAt={quote.customer_viewed_at || undefined} 
              paymentLinkViewed={!!quote.payment_link_viewed_at} 
              paymentLinkViewedAt={quote.payment_link_viewed_at || undefined} 
            />
          </div>
          
          <div className="flex items-center gap-1 md:gap-2">
            {quote.quote_file_url && (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleViewFile} 
                className="h-8 px-2 md:px-3 text-xs border-dashed hover:border-solid transition-all"
              >
                <FileText className="w-3 h-3 mr-1" />
                <span className="hidden sm:inline">Document</span>
                <span className="sm:hidden">Doc</span>
              </Button>
            )}
            
            {quote.quote_link && (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleViewLink} 
                className="h-8 px-2 md:px-3 text-xs border-dashed hover:border-solid transition-all"
              >
                <ExternalLink className="w-3 h-3 mr-1" />
                Link
              </Button>
            )}
            
            <QuoteActionsDropdown 
              quote={quote} 
              isAdmin={roles.isAdmin} 
              onReviseQuote={onReviseQuote}
              onAddVariation={onAddVariation} 
              onDeleteQuote={onDeleteQuote} 
              hasDownload={!!quote.quote_file_url} 
              onDownload={handleViewFile} 
            />
          </div>
        </div>
      </CardHeader>

      {!isQuoteFinalized && (
        <CardFooter className="pt-2 border-t border-border/30">
          <div className="w-full">
            <QuoteActionButtons 
              quote={quote} 
              canInteractWithQuotes={canInteractWithQuotes} 
              isAdminViewingOthersBooking={isAdminViewingOthersBooking} 
              loadingQuoteId={loadingQuoteId} 
              generatingPayment={generatingPayment} 
              onQuoteResponse={onQuoteResponse} 
            />
          </div>
        </CardFooter>
      )}
      
      {isQuoteFinalized && (
        <CardFooter className="pt-2 border-t border-border/30">
          <div className="w-full">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex items-center gap-2">
                <Badge variant={quote.status === 'accepted' ? 'default' : 'destructive'} className="text-xs">
                  Quote {quote.status === 'accepted' ? 'Accepted' : 'Declined'}
                </Badge>
                {quote.payment_confirmed && (
                  <Badge variant="default" className="text-xs bg-green-600">
                    Payment Confirmed
                  </Badge>
                )}
              </div>
              
              {isQuoteAccepted && (
                <QuotePaymentButtons 
                  quote={quote} 
                  onPaymentUpdate={onPaymentUpdate}
                />
              )}
            </div>
          </div>
        </CardFooter>
      )}
    </Card>
  );
};

export default QuoteCard;
